import { Stack, Box, Button } from '@mui/material';
import { useTitleHook } from 'hooks/page-title.hook';
import { PageHeader } from 'shared/ui/details-page-header';
import { ContractStatusStepper } from 'components/Contracts/contract-status-stepper';
import { LoadingBanner } from 'shared/ui/loading-banner';
import { formatLegalEntity } from 'utils/legal-entity-format-helpers';
import { ContractBasicsCard } from 'components/Contracts/contract-basic-card';
import { ErrorBanner } from 'shared/ui/error-banner';
import { FormData, formTools } from 'components/Contracts/contract-basic.form-tools';
import { useSnack } from 'shared/ui/snack';
import { ContractTabs } from 'components/Contracts/contract-tabs';
import { PrintContractButton } from 'features/contract/print-form-button';
import { useIdParam } from 'utils/url-paramters.hook';
import { useContract } from 'entities/contract/lib';
import { PartiesCard } from 'widgets/contracts/parties-card';
import ContractStatus from 'entities/contract/contract-status';
import { CompanyPermissions, CONTRACT_TYPE_CLIENT, SupplierPermissions } from '@sr/dto';
import { useUserAuth } from 'features/auth';
import useConfirmDialog from 'hooks/confirm-dialog.hook';
import { useState } from 'react';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

export const ContractDetailsPage = () => {
  const contractId = useIdParam('contractId');

  const { item: contract, error, updateItem, deactivateItem, activateItem } = useContract(contractId);
  const { user: currentUser } = useUserAuth();
  const showConfirm = useConfirmDialog();
  const [isDeactivating, setIsDeactivating] = useState(false);

  const contractNumber = contract?.name || `№ ${contract?.id}`;
  useTitleHook(`Договор ${contractNumber}`);

  const { showSuccess, showError } = useSnack();

  const handleUpdate = (formData: FormData) => {
    const dto = formTools.toUpdateDto(contract!.id, formData);
    return updateItem(dto)
      .then(() => showSuccess('Данные договора обновлены'))
      .catch((e) => showError(`Ошибка обновления данных договора: ${e}`));
  };

  const handleDeactivate = async () => {
    try {
      await showConfirm({
        title: 'Деактивировать договор?',
        content: 'После деактивации по этому договору нельзя будет выставлять новые счета.',
        rejectOnCancel: true
      });
    } catch {
      return;
    }

    setIsDeactivating(true);
    try {
      await deactivateItem();
      showSuccess('Договор деактивирован');
    } catch (e) {
      showError(`Ошибка деактивации договора: ${e}`);
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleActivate = async () => {
    setIsDeactivating(true);
    try {
      await activateItem();
      showSuccess('Договор активирован');
    } catch (e) {
      showError(`Ошибка активации договора: ${e}`);
    } finally {
      setIsDeactivating(false);
    }
  };

  if (!contract)
    if (error) return <ErrorBanner errorMessage={error} />;
    else return <LoadingBanner />;

  const isClientContract = contract.type === CONTRACT_TYPE_CLIENT;
  const canChangeActiveStatus =
    !!currentUser &&
    (isClientContract
      ? contract.manager?.id === currentUser.id || currentUser.permissions.includes(CompanyPermissions.ACCESS_ANY)
      : currentUser.permissions.includes(SupplierPermissions.EDIT));
  const canDeactivate = canChangeActiveStatus && contract.isActive;
  const canActivate = canChangeActiveStatus && contract.isDeactivated;

  return (
    <>
      <PageHeader
        title={
          <PageHeader.Text>
            {isClientContract ? 'Клиентский договор' : 'Договор поставщика'} <PageHeader.Text.Stressed>{contractNumber} </PageHeader.Text.Stressed>с компанией{' '}
            <PageHeader.Text.Stressed>{formatLegalEntity(isClientContract ? contract.buyer : contract.seller)}</PageHeader.Text.Stressed>
          </PageHeader.Text>
        }
        actions={
          <Stack direction="row" spacing={1}>
            {contract.type === CONTRACT_TYPE_CLIENT && <PrintContractButton allowPrintWithStamp={true} contractId={contractId} />}
            {canDeactivate && (
              <Button
                color="warning"
                variant="contained"
                startIcon={<BlockOutlinedIcon />}
                disabled={isDeactivating}
                onClick={handleDeactivate}
                sx={{ px: 2, fontWeight: 700, boxShadow: 2, whiteSpace: 'nowrap' }}
              >
                Деактивировать
              </Button>
            )}
            {canActivate && (
              <Button
                color="success"
                variant="contained"
                startIcon={<CheckCircleOutlineIcon />}
                disabled={isDeactivating}
                onClick={handleActivate}
                sx={{ px: 2, fontWeight: 700, boxShadow: 2, whiteSpace: 'nowrap' }}
              >
                Активировать
              </Button>
            )}
          </Stack>
        }
        subHeader={
          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
            <Box>
              <ContractStatus contract={contract} />
            </Box>
            <Box maxWidth={850} minWidth={800}>
              <ContractStatusStepper status={contract?.status} contractType={contract?.type} />
            </Box>
          </Box>
        }
      />

      <Stack direction="row" spacing={2}>
        <Box>
          <ContractBasicsCard contract={contract} updateContract={handleUpdate} />
        </Box>
        <Box>
          <PartiesCard contract={contract} />
        </Box>
        <Box flexGrow={1}>
          <ContractTabs contract={contract} />
        </Box>
      </Stack>
    </>
  );
};

export default ContractDetailsPage;
